<!-- Header: KPI stats + inline manual capture -->
<soft-ds-card class="my-4">
  <div class="flex flex-wrap items-center justify-between gap-x-8 gap-y-5">
    <div class="flex flex-wrap items-center gap-8">
      <div class="flex items-center gap-3">
        <span
          class="flex h-11 w-11 items-center justify-center rounded-lg bg-surface-secondary text-lg text-primary"
        >
          <soft-icon name="fa-regular fa-gauge" />
        </span>
        <div class="flex flex-col">
          <span class="text-overline">Kilometerstand</span>
          <span class="text-2xl font-bold leading-tight">
            @if (currentMileage() !== null) {
              {{ num(currentMileage()) | format: '1.0-0' }}
              <small class="text-sm font-medium text-light">km</small>
            } @else {
              <span class="text-base font-medium text-light">Keine erfasst</span>
            }
          </span>
        </div>
      </div>
      <div class="flex items-center gap-3">
        <span
          class="flex h-11 w-11 items-center justify-center rounded-lg bg-primary text-lg text-primary-contrast"
        >
          <soft-icon name="fa-regular fa-stopwatch" />
        </span>
        <div class="flex flex-col">
          <span class="text-overline">Betriebsstunden</span>
          <span class="text-2xl font-bold leading-tight text-primary-700">
            @if (currentOperatingHours() !== null) {
              {{ num(currentOperatingHours()) | format: '1.0-0' }}
              <small class="text-sm font-medium text-light">h</small>
            } @else {
              <span class="text-base font-medium text-light">Keine erfasst</span>
            }
          </span>
        </div>
      </div>
      <div class="flex items-center gap-3">
        <span
          class="flex h-11 w-11 items-center justify-center rounded-lg bg-surface-secondary text-lg text-primary"
        >
          <soft-icon name="fa-regular fa-clock-rotate-left" />
        </span>
        <div class="flex flex-col">
          <span class="text-overline">Einträge</span>
          <span class="text-2xl font-bold leading-tight">
            {{ mileage().length }}
            <small class="text-sm font-medium text-light">
              {{ mileage().length === 1 ? 'Eintrag' : 'Einträge' }}
            </small>
          </span>
        </div>
      </div>
    </div>
    <div class="flex flex-wrap items-end gap-3">
      <div class="flex flex-col gap-1">
        <span class="ds-caption">Kilometerstand</span>
        <soft-ds-number-input
          class="w-32"
          [(value)]="newMileage"
          icon="fa-regular fa-gauge"
          placeholder="km"
          [disabled]="createDisabled()"
        />
      </div>
      <div class="flex flex-col gap-1">
        <span class="ds-caption">Betriebsstunden</span>
        <soft-ds-number-input
          class="w-32"
          [(value)]="newOperatingHours"
          icon="fa-regular fa-stopwatch"
          placeholder="h"
          [disabled]="createDisabled()"
        />
      </div>
      <soft-ds-button
        variant="primary"
        (click)="submitManual()"
        [disabled]="createDisabled()"
      >
        <soft-icon name="fa-regular fa-plus" />
        {{ submitLabel() }}
      </soft-ds-button>
    </div>
  </div>
</soft-ds-card>

<!-- Entry timeline -->
@if (loading()) {
  <!-- loading skeletons -->
  <div class="flex flex-col gap-4">
    @for (row of [1, 2, 3]; track row) {
      <soft-ds-loading-skeleton
        width="100%"
        height="7.5rem"
        radius="var(--ds-radius-lg)"
      />
    }
  </div>
} @else if (error()) {
  <soft-ds-empty-state
    variant="error"
    icon="fa-regular fa-triangle-exclamation"
    title="Konnte nicht geladen werden"
    description="Die Kilometer-Historie konnte nicht geladen werden. Bitte versuchen Sie es später erneut."
  />
} @else if (mileage().length === 0) {
  <soft-ds-empty-state
    icon="fa-regular fa-clock-rotate-left"
    title="Keine Einträge"
    description="Es wurden noch keine Kilometerstände oder Betriebsstunden erfasst."
  />
} @else {
  <div class="flex flex-col gap-4">
    @for (entry of mileage(); track $index) {
      <soft-ds-card class="mileage-card overflow-hidden">

        <div
          class="flex flex-wrap items-center gap-4 border-b border-(--ds-border-default) px-4 py-3"
          [ngClass]="stripClass(entry)"
        >
          @if (entry.documentNumber !== null) {
            <span class="text-light">
              Beleg
              <strong class="text-foreground tabular-nums font-mono">
                {{ entry.documentNumber }}
              </strong>
            </span>
          }
          <soft-ds-badge [variant]="isManual(entry) ? 'warning' : 'success'" size="md">
            <soft-icon
              [name]="
                isManual(entry) ? 'fa-regular fa-pen' : 'fa-regular fa-clipboard-list'
              "
            />
            {{ isManual(entry) ? 'Manuell' : 'Auftrag' }}
          </soft-ds-badge>
          @if (hasNoReading(entry)) {
            <soft-ds-badge variant="secondary" size="md">
              <soft-icon name="fa-regular fa-circle-dashed" />
              Kein Stand
            </soft-ds-badge>
          }
          <span class="inline-flex items-center gap-1.5 font-bold text-foreground">
            <soft-icon name="fa-regular fa-calendar" />
            {{ entry.date | format: 'DATE.DEFAULT' }}
          </span>
          @if (entry.salesOrganisation) {
            <span class="inline-flex items-center gap-1.5 font-bold text-foreground">
              <soft-icon name="fa-regular fa-building" />
              {{ entry.salesOrganisation }}
            </span>
          }
          @if (!isManual(entry)) {
            <soft-ds-badge
              class="ml-auto"
              [variant]="statusVariant(entry.orderStatus)"
              size="md"
            >
              @if (isDone(entry.orderStatus)) {
                <span class="h-1.75 w-1.75 rounded-full bg-current"></span>
              }
              {{ statusLabel(entry.orderStatus) }}
            </soft-ds-badge>
          }
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-[11.25rem_1fr]">
          @if (hasNoReading(entry)) {
            <!-- no reading captured -->
            <div
              class="flex flex-col items-center gap-2 border-b border-(--ds-border-default) px-4 py-5 text-center sm:items-start sm:border-b-0 sm:border-r sm:text-left"
            >
              <span
                class="flex h-11 w-11 items-center justify-center rounded-xl border border-dashed border-primary text-lg text-primary"
              >
                <soft-icon name="fa-regular fa-gauge" />
              </span>
              <span class="text-sm font-bold text-foreground">Kein Stand erfasst</span>
              <span class="text-xs text-light">
                Bei diesem Eintrag wurde kein Zählerstand hinterlegt.
              </span>
            </div>
          } @else {
            <div
              class="flex flex-row gap-8 border-b border-(--ds-border-default) px-4 py-3.5 sm:flex-col sm:gap-3 sm:border-b-0 sm:border-r"
            >
              <div>
                <span
                  class="flex items-center gap-1.5 text-[0.6875rem] font-bold uppercase tracking-wide text-light"
                >
                  <soft-icon name="fa-regular fa-gauge" />
                  KM-Stand
                </span>
                <span
                  class="mt-0.5 block text-xl font-extrabold leading-snug text-foreground"
                >
                  @if (entry.mileage !== null) {
                    {{ num(entry.mileage) | format: '1.0-0' }}
                    <small class="text-sm font-medium text-light">km</small>
                  } @else {
                    <span class="text-sm font-medium text-light">Keine erfasst</span>
                  }
                </span>
              </div>
              <div>
                <span
                  class="flex items-center gap-1.5 text-[0.6875rem] font-bold uppercase tracking-wide text-light"
                >
                  <soft-icon name="fa-regular fa-stopwatch" />
                  Betriebsstunden
                </span>
                <span
                  class="mt-0.5 block text-xl font-extrabold leading-snug text-primary-700"
                >
                  @if (entry.operatingHours !== null) {
                    {{ num(entry.operatingHours) | format: '1.0-0' }}
                    <small class="text-sm font-medium text-light">h</small>
                  } @else {
                    <span class="text-sm font-medium text-light">Keine erfasst</span>
                  }
                </span>
              </div>
            </div>
          }

          <div class="flex min-w-0 flex-col gap-2 px-4 py-3.5">
            @if (entry.project?.key || entry.customer) {
              <div class="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                @if (entry.project?.key) {
                  <span class="font-bold text-primary">
                    Objekt {{ entry.project?.key }}
                    @if (entry.project?.name) {
                      &nbsp;· {{ entry.project?.name }}
                    }
                  </span>
                }
                @if (entry.customer) {
                  <span class="text-light">
                    Kunde {{ entry.customer.number }}
                    @if (entry.customer.name) {
                      &nbsp;· {{ entry.customer.name }}
                    }
                  </span>
                }
              </div>
            } @else {
              <span class="text-sm italic text-light">Keine Auftragsverknüpfung</span>
            }
            @if (entry.documentHeader?.trim()) {
              <span class="text-overline">Kopftext:</span>
              <div
                class="rounded-md border-(--ds-border-default) bg-surface-secondary px-3 py-2"
              >
                <p
                  class="m-0 mt-0.5 whitespace-pre-line text-sm leading-normal text-foreground"
                  [innerHTML]="entry.documentHeader"
                ></p>
              </div>
            }
            <span class="mt-auto inline-flex items-center gap-1.5 text-sm text-light">
              <soft-icon name="fa-regular fa-user" />
              Anwender
              <strong class="text-foreground">{{ entry.user }}</strong>
            </span>
          </div>
        </div>
      </soft-ds-card>
    }
  </div>
}
